import { useCallback } from "react";
import { useKeyboardFocus } from "./useKeyboardFocus";
import React from "react";

type FocusHandlingConfig = {
  isDisabled?: boolean;
  onFocusExtra?: () => void;
  onBlurExtra?: () => void;
  setHighlight: (value: boolean) => void;
  onEnter?: () => void;
};

export const useFocusHandling = ({
  isDisabled = false,
  onFocusExtra,
  onBlurExtra,
  setHighlight,
  onEnter,
}: FocusHandlingConfig) => {
  const { onFocus: keyboardFocus, onBlur: keyboardBlur } = useKeyboardFocus();
  const highlightRef = React.useRef(false);

  const handleFocus = useCallback(() => {
    if (!isDisabled) {
      keyboardFocus(() => {
        setHighlight(true);
        highlightRef.current = true;
        onFocusExtra?.();
      });
    }
  }, [isDisabled, keyboardFocus, setHighlight, onFocusExtra]);

  const handleBlur = useCallback(() => {
    if (!isDisabled) {
      keyboardBlur(() => {
        setHighlight(false);
        highlightRef.current = false;
        onBlurExtra?.();
      });
    }
  }, [isDisabled, keyboardBlur, setHighlight, onBlurExtra]);

  // Listen for Enter key and trigger onEnter if highlight is true
  React.useEffect(() => {
    if (isDisabled || !onEnter) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && highlightRef.current) {
        onEnter();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDisabled, onEnter]);

  return {
    handleFocus,
    handleBlur,
  };
};
